// Calendar arithmetic on YYYY-MM-DD strings. Dates never pass through a time zone.

const pad = (n: number) => String(n).padStart(2, '0');
const parts = (iso: string): [number, number, number] => [Number(iso.slice(0, 4)), Number(iso.slice(5, 7)), Number(iso.slice(8, 10))];
const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
export const ymd = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

export const monthStart = (iso: string) => `${iso.slice(0, 7)}-01`;
export function monthEnd(iso: string): string {
  const [y, m] = parts(iso);
  return ymd(y, m, daysInMonth(y, m));
}
/** The same day n months away, clamped to the month's last day (Mar 31 - 1 month = Feb 28). */
export function addMonths(iso: string, n: number): string {
  const [y, m, d] = parts(iso);
  const total = y * 12 + (m - 1) + n;
  const ny = Math.floor(total / 12), nm = (total % 12) + 1;
  return ymd(ny, nm, Math.min(d, daysInMonth(ny, nm)));
}
export function addDaysIso(iso: string, n: number): string {
  const [y, m, d] = parts(iso);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return ymd(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}
const dayNumber = (iso: string) => { const [y, m, d] = parts(iso); return Date.UTC(y, m - 1, d) / 86_400_000; };
export const quarterStart = (iso: string) => { const [y, m] = parts(iso); return ymd(y, Math.floor((m - 1) / 3) * 3 + 1, 1); };
export const isMonthEnd = (iso: string) => monthEnd(iso) === iso;
export function isValidDate(iso: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const [y, m, d] = parts(iso);
  return m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m);
}

export interface Range { from: string; to: string }
export type RangePreset = 'this-month' | 'last-month' | 'this-quarter' | 'last-quarter' | 'ytd' | 'last-year' | 'all';
/** The start of "all dates": earlier than any entry the books can hold. */
export const BOOKS_START = '1900-01-01';
export const RANGE_PRESETS: [RangePreset, string][] = [
  ['this-month', 'This month'], ['last-month', 'Last month'], ['this-quarter', 'This quarter'],
  ['last-quarter', 'Last quarter'], ['ytd', 'Year to date'], ['last-year', 'Last year'], ['all', 'All dates'],
];
export function rangeFor(preset: RangePreset, today: string): Range {
  const year = today.slice(0, 4);
  switch (preset) {
    case 'this-month': return { from: monthStart(today), to: monthEnd(today) };
    case 'last-month': { const s = addMonths(monthStart(today), -1); return { from: s, to: monthEnd(s) }; }
    case 'this-quarter': { const s = quarterStart(today); return { from: s, to: monthEnd(addMonths(s, 2)) }; }
    case 'last-quarter': { const s = addMonths(quarterStart(today), -3); return { from: s, to: monthEnd(addMonths(s, 2)) }; }
    case 'ytd': return { from: `${year}-01-01`, to: today };
    case 'last-year': return { from: `${Number(year) - 1}-01-01`, to: `${Number(year) - 1}-12-31` };
    case 'all': return { from: BOOKS_START, to: today };
  }
}
export function presetOf(range: Range, today: string): RangePreset | null {
  return RANGE_PRESETS.find(([p]) => { const r = rangeFor(p, today); return r.from === range.from && r.to === range.to; })?.[0] ?? null;
}

export type DatePreset = 'today' | 'end-last-month' | 'end-last-quarter' | 'end-last-year';
export const DATE_PRESETS: [DatePreset, string][] = [['today', 'Today'], ['end-last-month', 'End of last month'], ['end-last-quarter', 'End of last quarter'], ['end-last-year', 'End of last year']];
export function dateFor(preset: DatePreset, today: string): string {
  switch (preset) {
    case 'today': return today;
    case 'end-last-month': return addDaysIso(monthStart(today), -1);
    case 'end-last-quarter': return addDaysIso(quarterStart(today), -1);
    case 'end-last-year': return `${Number(today.slice(0, 4)) - 1}-12-31`;
  }
}
export const datePresetOf = (asOf: string, today: string): DatePreset | null => DATE_PRESETS.find(([p]) => dateFor(p, today) === asOf)?.[0] ?? null;

/**
 * The period just before a range. Whole calendar months step back by the same number of months
 * (Q3 → Q2, March → February); anything else steps back by the same number of days.
 */
export function previousPeriod(r: Range): Range {
  if (r.from === monthStart(r.from) && isMonthEnd(r.to)) {
    const months = (Number(r.to.slice(0, 4)) - Number(r.from.slice(0, 4))) * 12 + Number(r.to.slice(5, 7)) - Number(r.from.slice(5, 7)) + 1;
    const from = addMonths(r.from, -months);
    return { from, to: monthEnd(addMonths(from, months - 1)) };
  }
  const days = dayNumber(r.to) - dayNumber(r.from) + 1;
  return { from: addDaysIso(r.from, -days), to: addDaysIso(r.from, -1) };
}
/** The same dates a year earlier. A range ending on a month end still ends on one (Feb 29 → Feb 28). */
export function samePeriodLastYear(r: Range): Range {
  const to = addMonths(r.to, -12);
  return { from: addMonths(r.from, -12), to: isMonthEnd(r.to) ? monthEnd(to) : to };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** A short label for a column heading: "Mar 2026", "Q3 2026", "2025" or "Jan 1 – Oct 15, 2026". */
export function rangeLabel(r: Range): string {
  const [fy, fm, fd] = parts(r.from);
  const [ty, tm, td] = parts(r.to);
  if (r.from <= BOOKS_START) return `All dates to ${MONTHS[tm - 1]} ${td}, ${ty}`;
  const whole = fd === 1 && isMonthEnd(r.to);
  if (whole && fy === ty && fm === tm) return `${MONTHS[fm - 1]} ${fy}`;
  if (whole && fy === ty && fm === 1 && tm === 12) return String(fy);
  if (whole && fy === ty && (fm - 1) % 3 === 0 && tm === fm + 2) return `Q${(fm - 1) / 3 + 1} ${fy}`;
  const left = `${MONTHS[fm - 1]} ${fd}${fy === ty ? '' : `, ${fy}`}`;
  return `${left} – ${MONTHS[tm - 1]} ${td}, ${ty}`;
}

/** "Tuesday, April 7, 2026" for the greeting. */
export function longDate(iso: string): string {
  const [y, m, d] = parts(iso);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
